/**
 * usePdfPreview — the Reports area's preview-before-download lifecycle, shared
 * by the report pages (PRD_AttendanceSummaryReport C13/C14/C15, extended to
 * the DTR report at /test-impl 2026-09-08).
 *
 * Owns the one thing that is easy to get wrong and invisible when it is:
 * exactly ONE object URL is alive at a time. Extracted rather than copied
 * because two divergent copies of a revoke rule is precisely how a leak
 * survives a review — the second copy is the one nobody re-reads.
 *
 * Accepted residual (unchanged by the extraction): a generate still in flight
 * when the page unmounts leaks its one URL for the life of the tab. The
 * in-flight disable forecloses the double-generate race; the navigate-away
 * case costs one blob that dies with the tab.
 */
import { useState, useRef, useEffect, useCallback } from 'react';

/** The app's mobile breakpoint, matching EmployeeTimeRecords.js:189-196. */
export const MOBILE_MAX = 768;

export const usePdfPreview = () => {
  const [preview, setPreview] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_MAX);
  const previewRef = useRef(null);

  const clearPreview = useCallback(() => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    previewRef.current = null;
    setPreview(null);
  }, []);

  /**
   * Replace the live preview with a freshly built document. Revokes the
   * previous URL BEFORE creating the next one, which is what keeps the
   * one-at-a-time claim true rather than aspirational.
   *
   * @param {Object} doc - a jsPDF document (not saved by this hook)
   * @param {string} fileName - what Download PDF saves it as
   * @param {string} caption - the inputs it was built from, shown beside it
   */
  const showPreview = useCallback(
    (doc, fileName, caption) => {
      clearPreview();
      const next = { url: URL.createObjectURL(doc.output('blob')), doc, fileName, caption };
      previewRef.current = next;
      setPreview(next);
    },
    [clearPreview]
  );

  // Revoke on unmount. Reads the ref, not `preview`, so it does not re-run on
  // every regenerate and cannot capture a stale URL.
  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    },
    []
  );

  // A JS branch, not a CSS media query: jsdom has no matchMedia, so only this
  // shape is testable (LandingPage.js:244-248 guards for the same absence).
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { preview, isMobile, showPreview, clearPreview };
};
